import AnimationToggle from 'components/AnimationToggle';
import Brand from 'components/Brand';
import ButtonC, { BUTTON_HOVERSTYLE } from 'components/ButtonC';
import Header from 'components/Header';
import Account from 'components/Header/navigation/Account';
import Cart from 'components/Header/navigation/Cart';
import { LINK_TARGET } from 'components/Link';
import ProgressbarContainer from 'containers/ProgressbarContainer';
import StepperContainer from 'containers/StepperContainer';
import { VIEW } from 'helper/js/constants';
import {
  isAbTestVerticalScrollEnabled,
  isKauflandWebView,
  isLidlAppWebView,
  isRenderedInIFrame,
} from 'helper/js/helper';
import { useFlippyNavigate, useFlippyView } from 'helper/js/hooks/router';
import { hasAnimatedLinks } from 'providers/Animation/selectors';
import { LanguageContext } from 'providers/Language';
import { TrackingContext } from 'providers/Tracking';
import TRACKING_EVENTS from 'providers/Tracking/constants';
import TRACKING_EVENT_COLLECTIONS from 'providers/Tracking/trackingEventCollections';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

type Props = {
  hasError: boolean;
};

const HeaderContainer = ({ hasError }: Props) => {
  const {
    noMarginals,
    hasTopics,
    showSearch,
    shoppingCart: shoppingCartEnabled,
    myAccount: myAccountEnabled,
  } = useSelector((state: AppState) => state.settings);

  const { homeUrl, quitUrl } = useSelector((state: AppState) => state.appData.urls);
  const { isSmMin } = useSelector((state: AppState) => state.browser);
  const flippyView = useFlippyView();
  const isBetweenSMand850andMenuView = useSelector(
    (state: AppState) =>
      state.browser.isSmMin && state.browser.is850Max && flippyView === VIEW.MENU,
  );
  const hasSeenNotifcation = useSelector(
    (state: AppState) =>
      state.notifications.data.filter(
        (notification) => state.notifications.seen.indexOf(notification.id) <= -1,
      ).length > 0,
    () => true,
  );

  const hasAnimations = useSelector(hasAnimatedLinks);

  const { t } = useContext(LanguageContext);
  const { trackEventUniversalAnalytics, trackEventDataLayerService } = useContext(TrackingContext);
  const flippyNavigate = useFlippyNavigate();

  const updateBrowserUrl = useCallback((view) => flippyNavigate({ view }), [flippyNavigate]);

  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const topicButtonRef = useRef<HTMLButtonElement>(null);
  const [previousView, setPreviousView] = useState(flippyView);

  const isKauflandAppWebView = isKauflandWebView();

  const updateView = useCallback(
    (view: string) => updateBrowserUrl(flippyView !== view ? view : VIEW.FLYER),
    [flippyView, updateBrowserUrl],
  );

  const handleToggleStepper = useCallback(() => {
    trackEventUniversalAnalytics({
      ...TRACKING_EVENTS.OVERVIEW_BUTTON_CLICKED,
      eventLabel: 'header',
    });
    updateView(VIEW.OVERVIEW);
  }, [trackEventUniversalAnalytics, updateView]);

  const handleToggleJumpmarks = useCallback(() => {
    trackEventUniversalAnalytics(TRACKING_EVENTS.TOPICS_BUTTON_CLICKED);
    updateView(VIEW.JUMPMARKS);
  }, [trackEventUniversalAnalytics, updateView]);

  const handleToggleMenu = useCallback(() => {
    if (!hasError) {
      trackEventUniversalAnalytics(TRACKING_EVENTS.MENU_TOGGLE_CLICKED);
      trackEventDataLayerService(TRACKING_EVENT_COLLECTIONS.MENU_TOGGLE_BUTTON_CLICKED);
    } else {
      trackEventUniversalAnalytics({
        ...TRACKING_EVENTS.MENU_TOGGLE_CLICKED,
        eventCategory: 'flyer_error_page',
      });
    }
    updateView(VIEW.MENU);
  }, [hasError, trackEventDataLayerService, trackEventUniversalAnalytics, updateView]);

  const handleToggleSearch = useCallback(() => {
    trackEventUniversalAnalytics(TRACKING_EVENTS.SEARCH_BUTTON_CLICKED);
    trackEventDataLayerService(TRACKING_EVENT_COLLECTIONS.SEARCH_BUTTON_CLICKED);
    updateView(VIEW.SEARCH);
  }, [trackEventDataLayerService, trackEventUniversalAnalytics, updateView]);

  const handleExitFlyerButtonClicked = useCallback(() => {
    if (!hasError) {
      trackEventUniversalAnalytics(TRACKING_EVENTS.EXIT_FLYER_BUTTON_CLICKED);
    } else {
      trackEventUniversalAnalytics({
        ...TRACKING_EVENTS.EXIT_FLYER_BUTTON_CLICKED,
        eventCategory: 'flyer_error_page',
      });
    }
  }, [hasError, trackEventUniversalAnalytics]);

  const renderHeaderLeftContent = useMemo(
    () => (
      <>
        {!isRenderedInIFrame() && !isKauflandAppWebView && (
          <Brand href={homeUrl} hasError={hasError} />
        )}
        {!hasError && isSmMin && hasAnimations && <AnimationToggle />}
      </>
    ),
    [hasAnimations, hasError, homeUrl, isKauflandAppWebView, isSmMin],
  );

  const renderHeaderCenterContent = useMemo(
    () =>
      !hasError && (isSmMin || isKauflandAppWebView) ? (
        <StepperContainer onClick={handleToggleStepper} showArrowButtons={false} />
      ) : null,
    [handleToggleStepper, hasError, isKauflandAppWebView, isSmMin],
  );

  const linkTarget = isRenderedInIFrame() ? LINK_TARGET.TOP : LINK_TARGET.SELF;

  const renderHeaderRightContent = useMemo(
    () =>
      (import.meta.env.VITE_THEME !== 'kaufland' || !hasError) && (
        <>
          {import.meta.env.VITE_THEME !== 'kaufland' && (
            <ButtonC
              hoverStyle={BUTTON_HOVERSTYLE.ICON}
              inColumn
              icon="close"
              label={t('quit')}
              ariaLabel={`${t('quit')}, ${t('quit.ariaLabel')}`}
              ariaHidden={true}
              onClick={handleExitFlyerButtonClicked}
              href={quitUrl}
              target={linkTarget}
              transformLabelToUppercase={false}
              className="button--hide-label--sm"
            />
          )}
          {!hasError && showSearch && (
            <ButtonC
              ref={searchButtonRef}
              hoverStyle={BUTTON_HOVERSTYLE.ICON}
              inColumn
              icon={`magnifier--${import.meta.env.VITE_THEME}`}
              label={t('search')}
              ariaLabel={t('search.ariaLabel')}
              ariaHidden={true}
              onClick={handleToggleSearch}
              active={flippyView === VIEW.SEARCH}
              transformLabelToUppercase={false}
              className="button--hide-label--sm"
            />
          )}
          {!hasError && hasTopics && (
            <ButtonC
              hoverStyle={BUTTON_HOVERSTYLE.ICON}
              ref={topicButtonRef}
              inColumn
              icon={`bookmark--${import.meta.env.VITE_THEME}`}
              label={t('jumpmarksButton')}
              onClick={handleToggleJumpmarks}
              active={flippyView === VIEW.JUMPMARKS}
              transformLabelToUppercase={false}
              className="button--hide-label--sm"
            />
          )}
          {!hasError && myAccountEnabled && <Account ref={accountButtonRef} />}
          {!hasError && shoppingCartEnabled && <Cart />}
          <ButtonC
            hoverStyle={BUTTON_HOVERSTYLE.ICON}
            inColumn
            icon="bars-horizontal"
            ariaHidden={true}
            label={t('menu')}
            ref={menuButtonRef}
            indicator={hasSeenNotifcation}
            indicatorColor="danger"
            onClick={handleToggleMenu}
            active={flippyView === VIEW.MENU}
            transformLabelToUppercase={false}
            className="button--hide-label--sm"
            hasAriaExpanded={true}
          />
          {import.meta.env.VITE_THEME === 'kaufland' && !isKauflandAppWebView && (
            <ButtonC
              hoverStyle={BUTTON_HOVERSTYLE.ICON}
              inColumn
              icon="close"
              label={t('quit')}
              onClick={handleExitFlyerButtonClicked}
              href={quitUrl}
              target={LINK_TARGET.SELF}
              transformLabelToUppercase={false}
              className="button--hide-label--sm"
            />
          )}
        </>
      ),
    [
      flippyView,
      handleExitFlyerButtonClicked,
      handleToggleJumpmarks,
      handleToggleMenu,
      handleToggleSearch,
      hasError,
      hasSeenNotifcation,
      hasTopics,
      isKauflandAppWebView,
      linkTarget,
      myAccountEnabled,
      quitUrl,
      shoppingCartEnabled,
      showSearch,
      t,
    ],
  );

  useEffect(() => {
    setPreviousView(flippyView);
  }, [flippyView]);

  // Focus the search button when navigating from search to flyer view
  useLayoutEffect(() => {
    if (flippyView === VIEW.FLYER) {
      if (previousView === VIEW.SEARCH && searchButtonRef.current) {
        searchButtonRef.current?.focus();
      }

      if (previousView === VIEW.MENU && menuButtonRef.current) {
        menuButtonRef.current?.focus();
      }

      if (previousView === VIEW.JUMPMARKS && topicButtonRef.current) {
        topicButtonRef.current?.focus();
      }

      if (previousView === VIEW.JUMPMARKS && topicButtonRef.current) {
        topicButtonRef.current?.focus();
      }
      if (previousView === VIEW.ACCOUNT && accountButtonRef.current) {
        accountButtonRef.current?.focus();
      }
    }
    setPreviousView(flippyView);
  }, [flippyView, previousView]);

  const isAbTestVerticalScrollActive = isAbTestVerticalScrollEnabled(isSmMin);

  const renderBottomContent = <ProgressbarContainer />;
  return !noMarginals && !isLidlAppWebView() ? (
    <Header
      left={renderHeaderLeftContent}
      center={renderHeaderCenterContent}
      right={renderHeaderRightContent}
      bottom={renderBottomContent}
      hideLeft={false}
      hideCenter={isBetweenSMand850andMenuView}
      hideRight={isBetweenSMand850andMenuView}
      hasError={hasError}
      isSticky={isAbTestVerticalScrollActive}
    />
  ) : null;
};

export default memo(HeaderContainer);
